import { h, Component, render, Fragment } from 'preact';
import { createPortal } from 'preact/compat';
import { download } from 'network';
import { ANNOTATION_EVENT, PLAYER_EVENT } from 'constants/events';
import { BaseAnnotation } from './base_annotation';
import { ProgressIndicator } from './progress_indicator';
import { styleOptions } from 'media/video/videojs/style-options';
import { getSkin } from 'media/video/videojs/skins';
import { ControlButtons } from './control_buttons';
import { objectsEqual } from 'utilities/data';

export class ControlsAnnotation extends BaseAnnotation {
    constructor(...args) {
        super(...args);

        this.eventBus.onReplay(PLAYER_EVENT.AVAILABLE, (player, adapter) => {
            this.player = adapter;
            this.forceUpdate();
        });

        this.timeout = null;
    }

    applyControlBarThemeLabel() {
        const player = this.player;
        const prefix = 'hy-controlbar-skin-';
        player && player.applyThemeLabel(prefix, this.options.theme);
    }

    componentDidMount(props) {
        super.componentDidMount(props);
        this.applyControlBarThemeLabel();
    }

    componentDidUpdate(...args) {
        super.componentDidUpdate(...args);
        this.applyControlBarThemeLabel();
    }

    shouldComponentUpdate({ properties, dimensions }, { showComingSoon }) {
        return (
            !(objectsEqual(this.options, properties)) ||
            !(objectsEqual(this.props.dimensions, dimensions)) ||
             (showComingSoon !== this.state.showComingSoon)
        );
    }

    onButtonClick = e => {
        this.setState({
            showComingSoon: true
        });

        if (this.timeout) {
            clearTimeout(this.timeout);
        }

        this.timeout = setTimeout(() => {
            this.setState({
                showComingSoon: false
            });
        }, 2000);

        return;
    }

    get projectData() {
        const { project = {} } = this.data;
        return project;
    }

    get projectState() {
        const { state = {} } = this.data;
        return state;
    }

    render() {
        const Skin = getSkin(this.options.theme, this.options),
            style = {
                width: '100%',
                height: '100%',
                pointerEvents: 'none'
            },
            controlButtonAttrs = {
                projectData: this.projectData,
                projectState: this.projectState,
                list: this.options.buttons,
                onClick: this.onButtonClick,
                bus: this.eventBus,
                state: this.state,
                options: this.options,
                player: this.player,
                dimensions: this.props.dimensions || {}
            };

        return (
            <section className={ this.options.theme } style={ style }>
                { this.comingSoonDisplay }
                <Skin skinProps={this.options}>{ this.overrides }</Skin>
                { 
                    this.controlBarElement ? 
                        createPortal(this.controlBarComponents, this.controlBarElement) :
                        null
                }
            </section>
        );
    }

    get controlBarComponents() {
        const controlButtonAttrs = {
            projectData: this.projectData,
            projectState: this.projectState,
            list: this.options.buttons,
            onClick: this.onButtonClick.bind(this),
            bus: this.eventBus,
            state: this.state,
            options: this.options,
            player: this.player,
            dimensions: this.props.dimensions || {}
        };
        
        if (this.progressIndicator) {
            const progressAttrs = {
                ...this.props,
                ...this.progressIndicator,
                currentTime: this.player.currentTime,
                duration: this.player.duration
            }

            return (
                <Fragment>
                    <ControlButtons { ...controlButtonAttrs }/>
                    <ProgressIndicator {...progressAttrs} />
                </Fragment>
            )
        }

        return <ControlButtons { ...controlButtonAttrs }/>
    }

    get progressIndicator() {
        const { descendants = [] } = this.props;
        return descendants.find(d => d.type === 'progressindicator');
    }

    get comingSoonDisplay() {
        const { background } = this.options.colors,
            style = {
                position: 'absolute',
                transition: 'opacity 500ms ease',
                fontSize: '3em',
                width: '100%',
                height: '100%',
                color: 'white',
                textShadow: 'black 1px 1px 1px, black 0 0 1px',
                display: 'flex',
                textAlign: 'center',
                flexDirection: 'column',
                alignContent: 'center',
                justifyContent: 'center',
                opacity: this.state.showComingSoon ? 1 : 0
            };

        return <div aria-hidden="true" style={ style }>Feature Coming Soon</div>;
    }

    get overrides() {
        const { theme, colors, buttons } = this.options;

        return `
            ${styleOptions(this.options.options)}
        `;
    }

    get defaultState() {
        return {
            usersentiment: '',
            showComingSoon: false
        };
    }

    get options() {
        return {
            ...super.options,
            colors: super.options.colors || {},
            buttons: super.options.buttons || {},
            options: super.options.options || {}
        }
    }

    get controlBarElement() {
        return this.player && this.player.controlBar;
    }
}